import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import debounce from 'lodash.debounce'

import { MarkDown, Switch } from '@dfds-pax/shared-components/client'

import { useRoutes } from '../../Hooks/routes/useRoutes'
import { useVehicles } from '../../Hooks/vehicles/useVehicles'

import { ActiveSelectField, useActiveSelectField } from '../../context/ActiveSelectFieldProvider'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { isPetsOnlyAllowedInVehicle } from '../../Utils/passengers'
import OverlayFooter from '../shared/OverlayFooter'
import PassengerCounterRow from './PassengerCounterRow'

import { SelectedPassengers } from '../../Hooks/vehicles/vehicle-types'

type Props = {
  initialPassengers: SelectedPassengers
  onEscape?: () => void
  onFocusLeave?: () => void
  triggerRef?: React.RefObject<HTMLButtonElement | null>
}
const Passengers: React.FC<Props> = ({ initialPassengers, onEscape, onFocusLeave, triggerRef }: Props) => {
  const { t } = useTranslation()
  const { selectedRoute } = useRoutes()
  const { maxNumberOfPassengers, petsAllowed, vehicleApiData } = useVehicles()
  const { dispatch } = useTravelSearchContext()
  const { activeField, closeIfActive } = useActiveSelectField()
  const containerRef = useRef<HTMLDivElement>(null)

  const isOpen = activeField === ActiveSelectField.PASSENGER

  useEffect(() => {
    if (!isOpen) return
    containerRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus()
  }, [isOpen])

  const [passengers, setPassengers] = useState<SelectedPassengers>({
    adults: initialPassengers?.adults,
    children: initialPassengers?.children,
    childrenAges: initialPassengers?.childrenAges,
    pets: initialPassengers?.pets,
  })

  const maxAllowedPets = 5

  const debounceQuantityChange = useCallback(
    (adults: number, children: number, pets: number, childrenAges: number[]) => {
      const debouncedDispatch = debounce(() => {
        dispatch({
          passengers: { adults, children, childrenAges, pets },
          type: 'SET_PASSENGERS',
        })
      }, 400)
      debouncedDispatch()
    },
    [dispatch]
  )

  useEffect(() => {
    if (passengers.pets > vehicleApiData?.maxNumberOfPets) {
      const adjustedPets = vehicleApiData.maxNumberOfPets
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPassengers((prev) => ({ ...prev, pets: adjustedPets }))
      debounceQuantityChange(passengers.adults, passengers.children, adjustedPets, passengers.childrenAges)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleApiData?.maxNumberOfPets])

  useEffect(() => {
    if (!petsAllowed && passengers.pets > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPassengers((prev) => ({ ...prev, pets: 0 }))
      debounceQuantityChange(passengers.adults, passengers.children, 0, passengers.childrenAges)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petsAllowed])

  useEffect(() => {
    const total = passengers.adults + passengers.children
    if (maxNumberOfPassengers && total > maxNumberOfPassengers) {
      const adjustedChildren = Math.max(0, maxNumberOfPassengers - passengers.adults)
      const adjustedAges = passengers.childrenAges.slice(0, adjustedChildren)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPassengers({
        ...passengers,
        adults: maxNumberOfPassengers - adjustedChildren,
        children: adjustedChildren,
        childrenAges: adjustedAges,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxNumberOfPassengers])

  const handleChange = (field: keyof SelectedPassengers, value: number) => {
    const newPassengers = { ...passengers, [field]: value }
    if (field === 'children') {
      newPassengers.childrenAges =
        value > passengers.childrenAges.length
          ? [...passengers.childrenAges, ...Array(value - passengers.childrenAges.length).fill(8)]
          : passengers.childrenAges.slice(0, value)
    }
    setPassengers(newPassengers)
    debounceQuantityChange(newPassengers.adults, newPassengers.children, newPassengers.pets, newPassengers.childrenAges)
  }

  const handleAgeChange = (ages: number[]) => {
    setPassengers((prev) => ({ ...prev, childrenAges: ages }))
    debounceQuantityChange(passengers.adults, passengers.children, passengers.pets, ages)
  }

  const getPassengerErrorMessage = (max: number, route: string): string => {
    const key = `PASSENGERS-ERRORMESSAGE-${route}`
    const msg = t(key)
    return msg !== key ? msg : t('PASSENGERS-ERRORMESSAGE', { n1: max })
  }

  return (
    <div
      className="min-w-[260px] text-semantic-global-text-default md:min-w-[512px] lg:max-h-[calc(100dvh-160px)] lg:overflow-auto"
      onBlur={(event) => {
        // Clicking non-focusable content (labels, text) blurs to nothing, not to something outside the panel.
        if (!event.relatedTarget) return
        if (event.currentTarget.contains(event.relatedTarget)) return
        // The trigger's own click handler toggles the panel; closing here too would reopen it on a stale isOpen read.
        if (event.relatedTarget === triggerRef?.current) return
        onFocusLeave?.()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onEscape?.()
      }}
      ref={containerRef}
    >
      <ul className="m-0 list-none border-0 border-solid p-semantic-global-md">
        <PassengerCounterRow
          dataE2e="li-adults"
          description="+16"
          label={t('ADULTS')}
          maxValue={maxNumberOfPassengers - passengers.children}
          minValue={1}
          name="adults"
          onChange={(value) => handleChange('adults', value)}
          value={passengers.adults}
        />
        <PassengerCounterRow
          dataE2e="li-children"
          description="0-15"
          label={t('CHILDREN')}
          maxValue={maxNumberOfPassengers - passengers.adults}
          minValue={0}
          name="children"
          onChange={(value) => handleChange('children', value)}
          value={passengers.children}
        />
        {passengers.childrenAges.map((age, i) => (
          <PassengerCounterRow
            dataE2e="li-child-age"
            key={i}
            label={t('CHILD-AGE', { n1: i + 1 })}
            maxValue={15}
            minValue={0}
            name={`child-age-${i + 1}`}
            onChange={(value) => {
              const updatedAges = [...passengers.childrenAges]
              updatedAges[i] = value
              handleAgeChange(updatedAges)
            }}
            value={age}
          />
        ))}
        {selectedRoute && passengers.adults + passengers.children === maxNumberOfPassengers ? (
          <li className="m-0 mt-semantic-global-md list-none p-0" data-e2e="pax-error-message">
            {/* MarkDown hardcodes a text colour on its <p>, so the danger colour has to target it directly. */}
            <label className="phone-number mb-semantic-global-md leading-relaxed [&_p]:text-semantic-global-danger-default">
              <MarkDown>{getPassengerErrorMessage(maxNumberOfPassengers, selectedRoute.routeCode)}</MarkDown>
            </label>
          </li>
        ) : null}
      </ul>
      <div className="border-0 border-b border-solid border-semantic-global-border-light" />
      <div className="flex flex-row items-center gap-semantic-global-md p-semantic-global-md">
        <div className="flex flex-col">
          <span id="pets-toggle-label" className="font-medium text-semantic-global-text-default">
            {t('PETS-TOGGLE-TITLE')}
          </span>
          {petsAllowed ? (
            <div className="mt-semantic-global-xs text-sm font-normal text-semantic-global-text-default">
              <Trans count={maxAllowedPets} i18nKey="MAX-COUNT-PETS">
                {{ maxAllowedPets }}
              </Trans>
              . {selectedRoute && isPetsOnlyAllowedInVehicle(selectedRoute.routeCode) ? t('PETS-ONLY-ALLOWED-IN-VEHICLE') + '. ' : ''}
              {t('FEES-APPLY')}
            </div>
          ) : (
            <div className="mt-semantic-global-xs text-sm font-normal text-semantic-global-danger-default" data-e2e="pets-error-message">
              {t('PETS-ERRORMESSAGE')}
            </div>
          )}
        </div>
        <div className="ml-auto">
          <Switch
            aria-labelledby="pets-toggle-label"
            checked={passengers.pets > 0}
            disabled={!petsAllowed}
            name="pets"
            onChange={(checked) => handleChange('pets', checked ? 5 : 0)}
          />
        </div>
      </div>
      <div className="border-0 border-b border-solid border-semantic-global-border-light lg:hidden" />
      <OverlayFooter onClick={() => closeIfActive(ActiveSelectField.PASSENGER)} />
    </div>
  )
}

export default Passengers
